import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Dialog } from "@arcadeai/design-system/components/ui/atoms/dialog";
import type { SwitcherEntity } from "@arcadeai/ui-kit/org-project-context/organization-switcher";
import {
  ProjectSwitcher as BaseProjectSwitcher,
  type ProjectSwitcherProps as BaseProjectSwitcherProps,
} from "@arcadeai/ui-kit/org-project-context/project-switcher";
import { SwitcherItem } from "@arcadeai/ui-kit/org-project-context/switcher";
import { Pencil, Plus, Settings, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { CanOrg } from "@/app/capabilities/components/can-org";
import { ORG } from "@/app/capabilities/permissions";
import { useGetProjects } from "@/app/projects/api/hooks";
import { CreateProjectModal } from "@/app/projects/components/create-project-modal";
import { UpdateProjectModal } from "@/app/projects/components/update-project-modal";
import type { Project } from "@/app/projects/types";
import { ORG_PROJECT_ROUTE_PATH } from "@/lib/org-project-context/constants";
import {
  useDashboardOrgProject,
  useNavigateToScope,
  useOrgProjectRouteContext,
} from "@/lib/org-project-context/hooks";

export type ProjectSwitcherProps = Pick<
  BaseProjectSwitcherProps,
  "triggerClassName" | "triggerVariant" | "triggerSize"
> & {
  hideActions?: boolean;
};

export function ProjectSwitcher({
  hideActions = false,
  triggerClassName,
  triggerVariant = "outline",
  triggerSize = "sm",
}: ProjectSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  const { activeProjectId } = useDashboardOrgProject();
  const { organizationId } = useOrgProjectRouteContext();
  const { data: projects } = useGetProjects(organizationId);
  const navigateToScope = useNavigateToScope();

  const projectLookup = useMemo(
    () => new Map(projects.map((p) => [p.project_id, p])),
    [projects]
  );

  const entities: SwitcherEntity[] = useMemo(
    () =>
      projects.map((p) => ({
        id: p.project_id,
        name: p.name,
        description: p.description,
      })),
    [projects]
  );

  const handleSelect = useCallback(
    (targetProjectId: string) => {
      if (targetProjectId === activeProjectId) {
        setIsOpen(false);
        return;
      }

      navigateToScope({
        organizationId,
        projectId: targetProjectId,
      });
      setIsOpen(false);
    },
    [activeProjectId, navigateToScope, organizationId]
  );

  const renderItemAction = useCallback(
    (entity: SwitcherEntity) => {
      const project = projectLookup.get(entity.id);
      if (!project?.can_edit) {
        return null;
      }
      return (
        <Button
          className="hidden size-7 shrink-0 group-hover:flex"
          onClick={(e) => {
            e.stopPropagation();
            setEditingProject(project);
            setIsEditProjectOpen(true);
            setIsOpen(false);
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Pencil className="size-3.5" />
        </Button>
      );
    },
    [projectLookup]
  );

  if (!activeProjectId) {
    return null;
  }

  return (
    <>
      <BaseProjectSwitcher
        activeProjectId={activeProjectId}
        onOpenChange={setIsOpen}
        onSelect={handleSelect}
        open={isOpen}
        projects={entities}
        renderItemAction={renderItemAction}
        triggerClassName={triggerClassName}
        triggerSize={triggerSize}
        triggerVariant={triggerVariant}
      >
        {!hideActions && (
          <>
            <div className="px-2 pb-1 font-medium text-muted-foreground text-xs">
              Actions
            </div>
            <CanOrg permission={ORG.projects.create}>
              <SwitcherItem
                onSelect={() => setIsCreateProjectOpen(true)}
                value="action:create"
              >
                <Plus className="size-4 text-muted-foreground" />
                <span className="font-medium text-sm">Add project</span>
              </SwitcherItem>
            </CanOrg>
            <SwitcherItem
              from={ORG_PROJECT_ROUTE_PATH}
              to="./projects"
              value="action:manage"
            >
              <Settings className="size-4 text-muted-foreground" />
              <span className="font-medium text-sm">Manage projects</span>
            </SwitcherItem>
            <SwitcherItem
              from={ORG_PROJECT_ROUTE_PATH}
              to="./project/members"
              value="action:members"
            >
              <Users className="size-4 text-muted-foreground" />
              <span className="font-medium text-sm">Manage members</span>
            </SwitcherItem>
          </>
        )}
      </BaseProjectSwitcher>

      <Dialog onOpenChange={setIsCreateProjectOpen} open={isCreateProjectOpen}>
        <CreateProjectModal setIsOpen={setIsCreateProjectOpen} />
      </Dialog>

      <Dialog onOpenChange={setIsEditProjectOpen} open={isEditProjectOpen}>
        {!!editingProject && (
          <UpdateProjectModal
            key={editingProject.project_id}
            project={editingProject}
            setIsOpen={setIsEditProjectOpen}
          />
        )}
      </Dialog>
    </>
  );
}
